import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Flatpickr from "react-flatpickr";
import Select from "react-select";
import { FormFeedback, Label, Spinner } from "reactstrap";

import {
  LIVE_LECTURE_SLOT_DURATIONS,
  LiveLectureDto,
  LiveLectureSlotDuration,
} from "../../interfaces";
import { LiveLectureTypeEnum } from "../../enums";
import { useGetScheduleAvailableSlotsMutation } from "../../store/api/liveLectureApi";
import { getBrowserTimezone } from "../../utils/timezoneUtils";
import { useGetTeacherAdminSelectListQuery } from "../../store/api/teacherAdminApi";
import { SelectListDto } from "../../models";

export type SelectedScheduleSlot = {
  startAt: Date;
  endAt: Date;
  durationMinutes: number;
};

type TProps = {
  subjectId?: string;
  gradeId?: string;
  courseId?: string;
  courseDurationId?: string;
  lectureType: LiveLectureTypeEnum;
  teacherUserId: string;
  onTeacherChange: (teacherUserId: string, option: any) => void;
  teacherOption: any;
  selectedSlot: SelectedScheduleSlot | null;
  onSlotChange: (slot: SelectedScheduleSlot | null) => void;
  slotError?: string | null;
  disabled?: boolean;
  initialSlot?: SelectedScheduleSlot | null;
  lockTeacher?: boolean;
  excludeLiveLectureId?: string;
};

const clientTimezone = getBrowserTimezone();

const getCalendarDateKey = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const formatSlotTimeLabel = (startAt: string | Date, endAt: string | Date) => {
  const locale = document.documentElement.lang === "ar" ? "ar-KW" : "en-KW";
  const start = new Date(startAt);
  const end = new Date(endAt);

  return `${start.toLocaleTimeString(locale, {
    hour: "numeric",
    minute: "2-digit",
  })} - ${end.toLocaleTimeString(locale, {
    hour: "numeric",
    minute: "2-digit",
  })}`;
};

export const TeacherScheduleSlotPicker: React.FC<TProps> = ({
  subjectId,
  gradeId,
  courseId,
  courseDurationId,
  lectureType,
  teacherUserId,
  onTeacherChange,
  teacherOption,
  selectedSlot,
  onSlotChange,
  slotError,
  disabled = false,
  initialSlot,
  lockTeacher = false,
  excludeLiveLectureId,
}) => {
  const { t, i18n } = useTranslation();
  const [selectedDate, setSelectedDate] = useState<Date>(
    initialSlot?.startAt ? new Date(initialSlot.startAt) : new Date(),
  );
  const [slotDurationMinutes, setSlotDurationMinutes] =
    useState<LiveLectureSlotDuration>(
      (initialSlot?.durationMinutes as LiveLectureSlotDuration) || 60,
    );
  const [slots, setSlots] = useState<LiveLectureDto[]>([]);

  useEffect(() => {
    if (initialSlot) {
      setSelectedDate(new Date(initialSlot.startAt));
      setSlotDurationMinutes(
        initialSlot.durationMinutes as LiveLectureSlotDuration,
      );
    }
  }, [initialSlot]);

  const { data: teachers = [], isFetching: isFetchingTeachers } =
    useGetTeacherAdminSelectListQuery(subjectId, { skip: !subjectId });

  const [fetchSlots, { isLoading: isFetchingSlots }] =
    useGetScheduleAvailableSlotsMutation();

  const teacherOptions = useMemo(
    () =>
      teachers.map((teacher: SelectListDto) => ({
        label: i18n.language === "ar" ? teacher.value.ar : teacher.value.en,
        value: teacher.id,
        teacher,
      })),
    [teachers, i18n.language],
  );

  useEffect(() => {
    if (!subjectId || !teacherUserId) {
      setSlots([]);
      return;
    }

    fetchSlots({
      subjectId,
      teacherUserId,
      type: lectureType,
      startAt: getCalendarDateKey(selectedDate),
      endAt: getCalendarDateKey(selectedDate),
      timezone: clientTimezone,
      currentTime: new Date().toISOString(),
      slotDurationMinutes,
      gradeId,
      courseId,
      courseDurationId,
      excludeLiveLectureId,
    })
      .unwrap()
      .then((result: LiveLectureDto[]) => setSlots(result))
      .catch(() => setSlots([]));
  }, [
    subjectId,
    teacherUserId,
    lectureType,
    selectedDate,
    slotDurationMinutes,
    gradeId,
    courseId,
    courseDurationId,
    excludeLiveLectureId,
    fetchSlots,
  ]);

  const visibleSlots = useMemo(
    () =>
      slots.filter((slot) => {
        const startMs = new Date(slot.startAt).getTime();
        if (startMs >= Date.now()) {
          return true;
        }
        return (
          !!initialSlot &&
          Math.abs(startMs - new Date(initialSlot.startAt).getTime()) < 60000
        );
      }),
    [initialSlot, slots],
  );

  const isSlotSelected = (slot: LiveLectureDto) => {
    if (!selectedSlot) return false;
    return (
      Math.abs(
        new Date(slot.startAt).getTime() - selectedSlot.startAt.getTime(),
      ) < 60000
    );
  };

  return (
    <div className="mb-3">
      <div className="mb-3">
        <Label className="form-label">
          {t("teacher")}
          <span className="text-danger">*</span>
        </Label>
        {isFetchingTeachers ? (
          <div className="py-2">
            <Spinner size="sm" />
          </div>
        ) : (
          <Select
            isDisabled={disabled || !subjectId || lockTeacher}
            placeholder={t("selectTeacher")}
            value={teacherOption}
            onChange={(option: any) => {
              onTeacherChange(option?.value || "", option);
              onSlotChange(null);
            }}
            options={teacherOptions}
            noOptionsMessage={() => t("noTeachersWithSchedule")}
          />
        )}
      </div>

      {teacherUserId ? (
        <>
          <div className="mb-3">
            <Label className="form-label">{t("slotDuration")}</Label>
            <div className="d-flex flex-wrap gap-2">
              {LIVE_LECTURE_SLOT_DURATIONS.map((minutes) => (
                <button
                  key={minutes}
                  type="button"
                  disabled={disabled}
                  className={`btn btn-sm ${
                    slotDurationMinutes === minutes
                      ? "btn-primary"
                      : "btn-outline-primary"
                  }`}
                  onClick={() => {
                    setSlotDurationMinutes(minutes);
                    onSlotChange(null);
                  }}
                >
                  {minutes} {t("minutes")}
                </button>
              ))}
            </div>
          </div>

          <div className="mb-3">
            <Label className="form-label">{t("date")}</Label>
            <Flatpickr
              className="form-control dash-filter-picker shadow flatpickr-border"
              options={{
                dateFormat: "Y-m-d",
                minDate: new Date(),
              }}
              value={selectedDate}
              onChange={(dates) => {
                if (dates?.[0]) {
                  setSelectedDate(dates[0] as Date);
                  onSlotChange(null);
                }
              }}
              disabled={disabled}
            />
            <small className="text-muted d-block mt-1">
              {t("scheduleTimezoneDisplay", {
                timezone: clientTimezone,
              })}
            </small>
          </div>

          <div className="mb-2">
            <Label className="form-label">
              {t("availableSlots")}
              <span className="text-danger">*</span>
            </Label>
            {isFetchingSlots ? (
              <div className="py-3 text-center">
                <Spinner size="sm" />
              </div>
            ) : visibleSlots.length === 0 ? (
              <p className="text-muted mb-0">{t("noAvailableSlots")}</p>
            ) : (
              <div className="d-flex flex-wrap gap-2">
                {visibleSlots.map((slot) => (
                  <button
                    key={slot.id}
                    type="button"
                    disabled={disabled}
                    className={`btn btn-sm ${
                      isSlotSelected(slot)
                        ? "btn-primary"
                        : "btn-outline-secondary"
                    }`}
                    onClick={() =>
                      onSlotChange({
                        startAt: new Date(slot.startAt),
                        endAt: new Date(slot.endAt),
                        durationMinutes: slot.durationMinutes,
                      })
                    }
                  >
                    {formatSlotTimeLabel(slot.startAt, slot.endAt)}
                  </button>
                ))}
              </div>
            )}
          </div>
        </>
      ) : null}

      {slotError ? (
        <FormFeedback type="invalid" className="d-block">
          {slotError}
        </FormFeedback>
      ) : null}
    </div>
  );
};
